import { assert, NormalizedNumber } from '@sparkdotfi/common-universal'

import { useSpkTokenRepository } from '../../common/useSpkTokenRepository'
import { MyVotingPowerSidePanelProps } from '../components/my-voting-power-side-panel/MyVotingPowerSidePanel'
import { VotingPowerDelegationPanelProps } from '../components/voting-power-delegation-panel/VotingPowerDelegationPanel'
import { predefinedDelegates } from '../config'
import { delegateDialogConfig } from '../dialogs/delegate/DelegateDialog'
import { Proposal } from '../types'
import { useSpkGovernanceData } from './useSpkGovernanceData'
import { getChainConfigEntry } from '@/config/chain'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { useConnectModal } from '@/domain/hooks/wallet'
import { useOpenDialog } from '@/domain/state/dialogs/hooks'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { stakeDialogConfig } from '@/features/spk/staking/dialogs/stake/StakeDialog'

export interface UseSpkGovernanceResult {
  proposals: Proposal[]
  openConnectWalletModal: () => void
  isConnected: boolean
  votingPowerDelegationPanelProps: VotingPowerDelegationPanelProps
  myVotingPowerSidePanelProps: MyVotingPowerSidePanelProps
}

export function useSpkGovernance(): UseSpkGovernanceResult {
  const { openConnectModal = () => {} } = useConnectModal()
  const { account } = useConnectedAddress({ throwOnNotConnected: false })
  const { chainId } = usePageChainId()
  const isConnected = !!account
  const openDialog = useOpenDialog()

  const sparkTokenConfig = getChainConfigEntry(chainId).spk
  assert(sparkTokenConfig, 'Spark token config not available for this chain')

  const tokenRepository = useSpkTokenRepository({ chainId, account })
  const spk = tokenRepository.findOneBySymbol(TokenSymbol('SPK'))
  const spkBalance = tokenRepository.findOneBalanceOrZero(TokenSymbol('SPK'))
  const { proposals, votingPower, delegate, slashableStakedSpk } = useSpkGovernanceData({
    account,
    chainId,
    spk,
    spkBalance,
  })
  const preDelegationVotingPower = spkBalance.plus(slashableStakedSpk)

  const isDelegating = predefinedDelegates.some((d) => d.address === delegate)
  const isDelegatingToNotWhitelisted = Boolean(delegate && !predefinedDelegates.some((d) => d.address === delegate))
  const predefinedDelegate = predefinedDelegates.find((d) => d.address === delegate)

  function openDelegateDialog(): void {
    openDialog(delegateDialogConfig, {
      variant: 'standalone',
      action: 'delegate',
      successViewProceedText: 'Back to Governance',
    })
  }

  function openUndelegateDialog(): void {
    openDialog(delegateDialogConfig, {
      variant: 'standalone',
      action: 'undelegate',
      successViewProceedText: 'Back to Governance',
    })
  }

  function openStakeDialog(): void {
    openDialog(stakeDialogConfig, {
      variant: 'spk-staking',
      successViewProceedText: 'Back to Governance',
    })
  }

  const votingPowerDelegationPanelProps: VotingPowerDelegationPanelProps = (() => {
    if (!isConnected) {
      return {
        variant: 'no-delegation',
        isConnected: false,
        action: openConnectModal,
      }
    }

    if (!delegate) {
      return {
        variant: 'no-delegation',
        isConnected: true,
        action: openDelegateDialog,
      }
    }

    return {
      variant: 'delegation',
      delegateAddress: delegate,
      delegatedVotingPower: preDelegationVotingPower,
      openDelegateDialog,
      openUndelegateDialog,
      delegateAvatar: predefinedDelegate?.avatar,
      delegateName: predefinedDelegate?.name,
      spk,
      isDelegatingToNotWhitelisted,
    }
  })()

  const myVotingPowerSidePanelProps: MyVotingPowerSidePanelProps = (() => {
    if (preDelegationVotingPower.isZero() && votingPower.isZero()) {
      return {
        variant: 'no-voting-power',
      }
    }

    const displayedVotingPower = isDelegatingToNotWhitelisted
      ? NormalizedNumber.ZERO
      : votingPower.isZero()
        ? preDelegationVotingPower
        : votingPower

    return {
      variant: 'voting-power',
      votingPower: displayedVotingPower,
      slashableStakedSpk,
      spkBalance,
      openDelegateDialog,
      openUndelegateDialog,
      openStakeDialog,
      isDelegating,
      isDelegatingToNotWhitelisted,
      spk,
    }
  })()

  return {
    proposals,
    openConnectWalletModal: openConnectModal,
    isConnected,
    votingPowerDelegationPanelProps,
    myVotingPowerSidePanelProps,
  }
}
